import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { RequestStatus } from 'interfaces/RequestStatus';
import { fetchCertificateTypesArgument, fetchCertificateTypes } from './fetchCertificateTypes';
import { CertificateTypeInterface } from 'interfaces/CertificateType';


interface CertificatesState {
  certificateTypes: CertificateTypeInterface[];
  status: RequestStatus;
  error: string | null;
}

const initialState: CertificatesState = {
  certificateTypes: [],
  status: RequestStatus.idle,
  error: null,
};

export const getCertificateTypes = createAsyncThunk("certificateTypes/fetchCertificateTypes", async (args: fetchCertificateTypesArgument, { getState }) => {
  const { user } = getState() as RootState;
  const response = await fetchCertificateTypes({ ...args, endpoint: user.role });
  return { headers: response.headers, data: response.data, status: response.status };
});

const certificateTypesSlice = createSlice({
  name: 'certificateTypes',
  initialState,
  reducers: {
    setCertificateTypes: (state, action: PayloadAction<CertificateTypeInterface[]>) => {
      state.certificateTypes = action.payload;
      state.status = RequestStatus.idle;
    },
    setStatus: (state, action: PayloadAction<CertificatesState['status']>) => {
      state.status = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCertificateTypes.pending, (state) => {
        state.status = RequestStatus.loading;
      })
      .addCase(getCertificateTypes.fulfilled, (state, action) => {
        state.status = RequestStatus.idle;
        state.certificateTypes = action.payload.data.data.certificate_types;
      })
      .addCase(getCertificateTypes.rejected, (state, action) => {
        state.status = RequestStatus.failed;
        state.error = action.error.message || 'Failed to fetch certificate types';
      });
  },
});

export const { setCertificateTypes, setStatus, setError } = certificateTypesSlice.actions;

export const selectCertificateTypes = (state: RootState) => state.certificateTypes.certificateTypes;
export const selectCertificatesStatus = (state: RootState) => state.certificateTypes.status;
export const selectCertificatesError = (state: RootState) => state.certificateTypes.error;

export default certificateTypesSlice.reducer;